import React, { FC, useRef } from 'react'
import { Property } from 'csstype'
import styled, { css } from 'styled-components'
import { Field as FinalField, UseFieldConfig } from 'react-final-form'
import { useFocus } from '@chilipiper/utils'
import { Flex } from '../../new/core-components/box'
import { VerticalDivider } from '../../new/core-components/divider'
import { ThemeColors } from '../../new/theme/colors'
import { ThemeFontSizes } from '../../new/theme/typography'
import { Button, ButtonProps } from '../button'
import { Card } from '../card'
import { Field, Props as FieldProps } from './Field'

/** @deprecated - See {@link TextField} */
export type TextFieldProps = Omit<FieldProps, 'onChange'> & {
  hideNumberArrows?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  textOverflow?: Property.TextOverflow
}

const InputStyled = styled.input<{
  fontSize?: ThemeFontSizes
  placeholderColor?: ThemeColors
  textOverflow?: Property.TextOverflow
}>`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: none;
  padding: 0;
  font-weight: 500;
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors['text/form-placeholder'] : theme.colors['text/form-value']};
  font-size: ${props => props.theme.fontSizes[props.fontSize ?? 14]};
  font-family: ${props => props.theme.fonts['primary']};
  ${({ textOverflow }) =>
    textOverflow &&
    css`
      text-overflow: ${textOverflow};
    `}
  ${({ theme }) =>
    css`
      ::placeholder {
        color: ${theme.colors['text/form-placeholder']};
        font-size: ${props => props.theme.fontSizes[14]};
        font-weight: 500;
      }
    `}
`

/**
 * @deprecated Use the new Input from `@chilipiper/design-system/src/new` instead if
 *   labelPlacement='outside' or no label and LabelInput from `@chilipiper/design-system/src/new`
 *   otherwise
 */
export const TextField = React.forwardRef(
  (
    {
      focused: focus,
      onFocus,
      onBlur,
      value,
      defaultValue,
      onChange,
      placeholder,
      id,
      name,
      'aria-label': ariaLabel,
      'data-test-id': dataTestId,
      label,
      fontWeight,
      fontSize,
      h,
      autoFocus,
      textOverflow,
      roundedBottomCorners = true,
      ...p
    }: TextFieldProps,
    ref
  ) => {
    const [focused, handleFocus, handleBlur] = useFocus(focus, onFocus, onBlur)
    return (
      <Field
        {...p}
        roundedBottomCorners={roundedBottomCorners}
        fontWeight={fontWeight}
        id={id}
        h={h || (label ? 11 : 8)}
        label={label}
        focused={focused}
        value={value}
        placeholder={placeholder}
      >
        <InputStyled
          {...p}
          h={h}
          label={label}
          data-test-id={dataTestId}
          aria-label={ariaLabel}
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          id={id}
          name={name}
          placeholder={placeholder}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          fontWeight={fontWeight}
          fontSize={fontSize}
          textOverflow={textOverflow}
        />
      </Field>
    )
  }
)

TextField.displayName = 'TextField'

/** @deprecated - See {@link FinalTextField} */
export type FinalTextFieldProps = Omit<TextFieldProps, 'name'> & { name: string } & Pick<
    UseFieldConfig<string>,
    'parse'
  >

/**
 * @deprecated Use the new Input from `@chilipiper/design-system/src/new` instead if
 *   labelPlacement='outside' or no label and LabelInput from `@chilipiper/design-system/src/new`
 *   otherwise
 */
export const FinalTextField: FC<FinalTextFieldProps> = ({
  name,
  id,
  parse,
  onFocus,
  ...props
}: FinalTextFieldProps) => {
  return (
    <FinalField name={name} id={id} parse={parse}>
      {({ input, meta }) => (
        <TextField
          {...props}
          {...input}
          id={id}
          error={meta.touched && meta.error}
          onFocus={e => {
            onFocus?.(e)
            input.onFocus(e)
          }}
        />
      )}
    </FinalField>
  )
}

type TextFieldWithFloatingButton = {
  buttonLabel: string
  buttonProps?: ButtonProps
  css?: any
  secondButton?: React.ReactNode
} & TextFieldProps

type TextFieldWithCopyProps = {
  onCopy?: () => void
  secondButton?: React.ReactNode
} & TextFieldProps

const ButtonContainer = styled(Card)`
  position: absolute;
  right: 8px;
  top: calc(50% - 20px);
  padding: 0;
  display: flex;
`

const TextFieldWithFloatingButton = React.forwardRef<HTMLInputElement, TextFieldWithFloatingButton>(
  ({ secondButton: SecondButton, onClick, buttonLabel, buttonProps, ...props }, ref) => {
    return (
      <Flex position='relative' w='100%'>
        <TextField w='100%' ref={ref} {...props} />
        <ButtonContainer h={8}>
          <Button
            borderRadius='none'
            boxShadow='none'
            bg='transparent'
            color='text/action-moderate'
            onClick={onClick}
            {...buttonProps}
          >
            {buttonLabel}
          </Button>
          {SecondButton && (
            <>
              <VerticalDivider />
              {SecondButton}
            </>
          )}
        </ButtonContainer>
      </Flex>
    )
  }
)
TextFieldWithFloatingButton.displayName = 'TextFieldWithFloatingButton'

const TextFieldWithCopy = ({
  onCopy,
  secondButton: AdditionalButton,
  ...props
}: TextFieldWithCopyProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const onCopyClick = () => {
    inputRef?.current?.select()
    inputRef?.current?.focus()
    document.execCommand('copy')
    onCopy?.()
  }

  return (
    <TextFieldWithFloatingButton
      ref={inputRef}
      readOnly
      buttonLabel='Copy'
      onClick={onCopyClick}
      secondButton={AdditionalButton}
      {...props}
    />
  )
}

type TextFieldWithCopyAndPreviewProps = {
  link?: string
  value: string
} & TextFieldWithCopyProps

export const TextFieldWithCopyAndPreview = (props: TextFieldWithCopyAndPreviewProps) => {
  return (
    <TextFieldWithCopy
      {...props}
      secondButton={
        <a href={props.link || props.value || '#'} rel='noopener noreferrer' target='_blank'>
          <Button boxShadow='none' borderRadius='none' bg='transparent' as='div'>
            Preview
          </Button>
        </a>
      }
    />
  )
}
